import React, { useState, useRef, useEffect } from 'react';
import styles from './styles/TableOfContentsModal.module.css';
import { FaSave, FaTimes, FaInfoCircle } from 'react-icons/fa';

const TableOfContentsModal = ({
    isVisible,
    toggleModal,
    itemLabel,
    setItemLabel,
    situations,
    selectedSituation,
    setSelectedSituation,
    handleSave,
    isEditing,
}) => {
    const modalRef = useRef(null);
    const inputRef = useRef(null);
    const [showInfoTooltip, setShowInfoTooltip] = useState(false);

    useEffect(() => {
        if (isVisible) {
            
            setTimeout(() => {
                inputRef.current?.focus();
            }, 0);
        }
    }, [isVisible]);


    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isVisible && modalRef.current && !modalRef.current.contains(event.target)) {
                toggleModal(); 
            }
        };

        if (isVisible) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isVisible, toggleModal]);


    if (!isVisible) return null;


    const isSaveDisabled = !itemLabel?.trim() || !selectedSituation;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent} ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="modalTitle">

                <h3 id="modalTitle" className={styles.modalTitle}>
                    {isEditing ? 'Edit Table of Contents Item' : 'Add Table of Contents Item'}
                </h3>

                <div className={styles.labelContainer}>
                    <label htmlFor="itemLabelInput" className={styles.label}>
                        Item Label <span className={styles.requiredIndicator}>*</span>
                    </label>
                    <div className={styles.infoIconContainer}>
                         <button
                            type="button"
                            className={`${styles.button} ${styles.buttonIcon} ${styles.infoButton}`}
                            onMouseEnter={() => setShowInfoTooltip(true)}
                            onMouseLeave={() => setShowInfoTooltip(false)}
                            onFocus={() => setShowInfoTooltip(true)}
                            onBlur={() => setShowInfoTooltip(false)}
                            aria-describedby="labelInfoTooltip"
                            title="About Item Labels"
                         >
                            <FaInfoCircle />
                        </button>
                        {showInfoTooltip && (
                            <div id="labelInfoTooltip" className={styles.infoTooltip} role="tooltip">
                                This is the label end-users will see and click in the Table of Contents. It can be different from your Situation Title (which only you see). Use clear, descriptive labels for navigation.
                            </div>
                        )}
                    </div>
                </div>
                <input
                    id="itemLabelInput"
                    ref={inputRef}
                    type="text"
                    placeholder="Enter label visible to users"
                    value={itemLabel}
                    onChange={(e) => setItemLabel(e.target.value)}
                    className={styles.inputField}
                    aria-describedby="situationSelectionInfo"
                    required
                />

                 <p id="situationSelectionInfo" className={styles.label}>
                     Link to Situation: <span className={styles.requiredIndicator}>*</span>
                 </p>
                <div className={styles.situationsListWrapper}>
                    <ul className={styles.situationsList}>
                        {(!situations || situations.length === 0) && (
                            <li className={styles.noSituations}>
                                No situations available in this scenario.
                            </li>
                        )}
                        {Array.isArray(situations) && situations.map((situation) => (
                            <li
                                key={situation._id}
                                className={`${styles.situationItem} ${selectedSituation?._id === situation._id ? styles.selectedSituation : ''}`}
                                onClick={() => setSelectedSituation(situation)}
                                tabIndex={0}
                                role="option"
                                aria-selected={selectedSituation?._id === situation._id}
                                onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedSituation(situation); }}
                            >
                                {(situation.title && situation.title.trim()) || "Untitled Situation"}
                            </li>
                        ))}
                    </ul>
                 </div>

                <div className={styles.buttonGroup}>
                    <button
                        onClick={handleSave}
                        disabled={isSaveDisabled}
                        className={`${styles.button} ${styles.buttonPrimary}`}
                        title={isSaveDisabled ? "Enter a label and select a situation to save" : "Save changes"}
                    >
                         <FaSave /> {isEditing ? 'Save Changes' : 'Add Item'}
                    </button>
                    <button
                        onClick={toggleModal}
                        className={`${styles.button} ${styles.buttonSecondary}`}
                    >
                        <FaTimes /> Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TableOfContentsModal;